import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import ConditionalWarningPopupButton from 'components/molecules/buttons/conditional-warning-popup-button/ConditionalWarningPopupButton';
import { Form } from 'components/organisms';
import { artistInitialValues } from 'components/organisms/forms/artist/ArtistForm';
import { listQueryKeys } from 'constants/queryKey';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useArtistGoBack } from 'hooks/useArtistGoBack';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetArtist, useUpdateArtist } from 'services/artists';
import {
  useChangeProfilePicture,
  useCreateProfilePicture,
  useDeleteProfilePicture,
} from 'services/artists/profilePicture';
import { IArtistMutationForm } from 'services/artists/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const EditArtist = () => {
  const artistId = useParamId('artist');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { musicProductDigitalForm, clearForms, clearForm, setForm, trackForm } = useStoreState();

  const queryClient = useQueryClient();

  const artist = useGetArtist(artistId);
  const updateArtist = useUpdateArtist();
  const changeProfilePicture = useChangeProfilePicture();
  const createProfilePicture = useCreateProfilePicture();
  const deleteProfilePicture = useDeleteProfilePicture(artistId);

  const mutateOptions = useMutateOptions<IArtistMutationForm>();

  const { onGoBack } = useArtistGoBack();

  const onUpdateArtist = async (
    values: IArtistMutationForm,
    helpers: FormikHelpers<IArtistMutationForm>,
  ) => {
    const updatedArtist = middleware.artist.formToQuery(values);

    let artistVersion = values.version;

    if (values.file) {
      artist.data?.profilePictureLink?.length
        ? await changeProfilePicture.mutateAsync(
            {
              id: artistId,
              file: values.file,
              version: values.version,
            },
            {
              onError: (error) => onErrorShowAllErrorsToast(error),
              onSuccess: (a) => {
                artistVersion = a.version;
              },
            },
          )
        : await createProfilePicture.mutateAsync(
            {
              id: artistId,
              file: values.file,
              version: values.version,
            },
            {
              onError: (error) => onErrorShowAllErrorsToast(error),
              onSuccess: (a) => {
                artistVersion = a.version;
              },
            },
          );
    }

    if (artist.data?.profilePictureLink?.length !== values.profilePictureLink?.length) {
      await deleteProfilePicture.mutateAsync(undefined, {
        onError: (error) => onErrorShowAllErrorsToast(error),
        onSuccess: (a) => {
          artistVersion = a.version;
        },
      });
    }

    await updateArtist.mutateAsync(
      { artistId: artistId, ...updatedArtist, version: artistVersion },
      {
        ...mutateOptions(helpers),
        onSuccess: async (artist) => {
          onGoBack(artistId, artist);
          await queryClient.invalidateQueries({
            queryKey: [listQueryKeys.artists],
          });
        },
      },
    );
  };

  return (
    <Form.Artist
      i18nextPrefix="artist"
      initialValues={artist.data ? middleware.artist.queryToForm(artist.data) : artistInitialValues}
      onSubmit={onUpdateArtist}
      tittle={artist.data?.nickname ?? '...'}
      customBackItem={
        <ConditionalWarningPopupButton
          title={t('popup.form-cancelation-title')}
          message={t('popup.form-cancelation-description')}
          confirmButtonVariant={BUTTON_VARIANT.background}
          showWarning={!musicProductDigitalForm && !trackForm}
          onConfirm={() => onGoBack(artistId)}
          isClear
          onlyIcon
          disabled={!artist.data}
        >
          <Button.Icon icon={<PiX />} />
        </ConditionalWarningPopupButton>
      }
      status={{ isLoading: artist.isLoading }}
      // disableTabs={[ArtistTabs.bio]}
    />
  );
};

export default EditArtist;
